
import {of as observableOf, Observable, Subscription} from 'rxjs';

import {delay} from 'rxjs/operators';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {validateAllFormFields} from "@logbook/shared";
import {StateStorageService} from "@logbook/core/auth/services/state-storage.service";
import {MixpanelService} from "@logbook/core/tracking/mixpanel.service";
import {MixpanelEvent} from "@logbook/core/tracking/mixpanel-event.enum";
import {LoginService} from "@logbook/core/auth/services/login.service";

@Component({
    selector: 'dln-home',
    templateUrl: './login.component.html',
    styleUrls: [
        'login.scss'
    ]
})
export class LoginComponent implements OnInit, OnDestroy {
    loginForm: FormGroup;
    authenticationError: string;
    authenticationLogout: boolean;

    private authenticationLogoutDelay: Subscription = null;

    constructor(private loginService: LoginService,
                private router: Router,
                private route: ActivatedRoute,
                private stateStorageService: StateStorageService,
                private mixpanelService: MixpanelService) {
    }

    ngOnInit(): void {
        this.loginService.logout();
        if (this.route.snapshot.queryParams) {
            this.authenticationLogout = this.route.snapshot.queryParams["logout"];
            this.authenticationLogoutDelay = observableOf(true).pipe(delay(1500))
                .subscribe(() => this.authenticationLogout = false);
            if (this.authenticationLogout) {
                this.mixpanelService.reset();
            }
        }

        const {required} = Validators;

        this.loginForm = new FormGroup({
            email: new FormControl(null, [
                required
            ]),
            password: new FormControl(null, [
                required
            ]),
            rememberMe: new FormControl(false)
        });
    }

    ngOnDestroy() {
        if (this.authenticationLogoutDelay) {
            this.authenticationLogoutDelay.unsubscribe();
        }
    }

    login() {
        if (this.loginForm.valid) {
            this.loginForm.markAsPending();
            this.loginService.login({
                username: this.email.value,
                password: this.password.value,
                rememberMe: this.rememberMe.value
            }).then(() => {
                const redirect = this.stateStorageService.getUrl();
                this.mixpanelService.identify(this.email.value);
                this.mixpanelService.track(MixpanelEvent.LOG_IN, {
                    username: this.email.value,
                    rememberMe: this.rememberMe.value
                });
                if (redirect) {
                    this.router.navigateByUrl(redirect);
                } else {
                    this.router.navigate(["/logs"]);
                }
            }).catch((reason) => {
                let causeFailed;
                this.loginForm.updateValueAndValidity();
                if (reason.status && reason.status === 401) {
                    const body = reason.error;
                    if (body.AuthenticationException && (body.AuthenticationException === 'Le compte utilisateur est désactivé'
                            || body.AuthenticationException === "L'utilisateur n'appartient à aucune team")) {
                        this.authenticationError = "<div>Your account needs to be activated.</div>" +
                            "<div> Please contact teamlogs support at <b>contact@teamlogs.net</b></div>";
                    } else {
                        this.authenticationError = "Bad password or username";
                    }
                } else {
                    causeFailed = "Erreur interne";
                    this.authenticationError = "There is a problem please try again later";
                }
                this.mixpanelService.track(MixpanelEvent.LOG_IN_FAILED, {
                    username: this.email.value,
                    cause: causeFailed
                });
            });
        } else {
            validateAllFormFields(this.loginForm);
        }
    }

    get email() {
        return this.loginForm.get('email');
    }

    get password() {
        return this.loginForm.get('password');
    }

    get rememberMe() {
        return this.loginForm.get('rememberMe');
    }
}
